declare const require: any;
import { enableProdMode } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { AMPLITUDE_CONFIGURATION, AmplitudeConfiguration, LOG_ROCKET_CONFIGURATION, LogRocketConfiguration } from '@uzurv/core';
import { UZURV_REST_CONFIGURATION, UzurvRestConfiguration } from '@uzurv/http';
import { UZURV_API_CONFIGURATION } from '@uzurv/rideWss/configurations/uzurv-api.configuration';
import { BUG_SNAG_CONFIGURATION, BugsnagConfiguration } from 'projects/sdk/packages/core/src/interfaces/bugsnag-configuration';
import { APP_CONFIGURATION, AppConfiguration } from './app/app-configuration';
import { AppModule } from './app/app.module';
import { environment } from './environments/environment';
import { AppComponent as RideWssAppComponent } from './ride-wss-app/app/app.component';
import { appConfig as rideWssAppConfig } from './ride-wss-app/app/app.config';
import { ParseUtil } from './utils/parse/parse.util';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const version = require('../../../package.json').version;

if (environment.production) {
  enableProdMode();
}

const main = async () => {
  const rideWssUrlRegex: RegExp = /^ride[a-z0-9-]*\.uzurv\.com$/;

  function isRideWssUrl(pathname: string) {
    return rideWssUrlRegex.test(pathname);
  }

  try {
    let vendorName = 'uzurv';

    if (environment.envName === 'deployed') {
      const subdomain = window.location.hostname.split('.')[0];

      if (!subdomain.includes('crm')) {
        vendorName = subdomain;
      }
    }

    const headers = new Headers();
    headers.append('pragma', 'no-cache');
    headers.append('cache-control', 'no-cache');

    const init = {
      method: 'GET',
      headers: headers
    };

    let data = await fetch(`/assets/configurations/config.json`, init);
    const environmentData = await data.json();

    if (environmentData.envName !== 'prod') {
      const index = vendorName.lastIndexOf(environmentData.envName);
      if (index !== -1) {
        vendorName = vendorName.slice(0, index);
      }
    }

    data = await fetch(`/assets/themes/${vendorName}/config.json`, init);
    const themeConfiguration = await data.json();

    const restConfiguration: UzurvRestConfiguration = {
      claraApiUrl: environmentData.restApiUrl,
      dotsApiUrl: environmentData.dotsApiUrl,
      googleMapsApiKey: environmentData.googleMapsApiKey,
      reportingApiUrl: environmentData.reportingApiUrl,
      httpDebugMode: false
    };

    const bugsnagConfiguration: BugsnagConfiguration = {
      bugsnagApiKey: environmentData.bugsnagApiKeyWeb,
      autoCaptureSessions: environmentData.bugsnagAutoCaptureSessions,
      appVersion: environmentData.production ? version : version.split('-')[0],
      notifyReleaseStages: ['dev', 'qa', 'qa2', 'staging', 'training', 'prod'],
      enableRemoteErrorLogging: environmentData.bugsnagEnableRemoteErrorLogging,
      releaseStage: environmentData.envName
    };

    const applicationConfiguration: AppConfiguration = {
      envName: environmentData.envName,
      production: environmentData.production,
      version: environmentData.production ? version : version.split('-')[0],
      webApplicationUrl: environmentData.webApplicationUrl,
      googleMapsApiKey: environmentData.googleMapsApiKey,
      listPollingSeconds: environmentData.listPollingSeconds,
      operationsAlertPollingSeconds: environmentData.operationsAlertPollingSeconds,
      authTimeoutMinutes: environmentData.authTimeoutMinutes,
      operationsPhoneNumber: environmentData.operationsPhoneNumber,
      supportPhoneNumber: environmentData.supportPhoneNumber,
      name: themeConfiguration.name,
      shortName: themeConfiguration.shortName,
      logo: themeConfiguration.logo,
      googleAnalyticsMeasurementId: environmentData.googleAnalyticsMeasurementId
    };

    const logRocketConfiguration: LogRocketConfiguration = {
      logRocketProjectKey: environmentData.logRocketProjectKey,
      logRocketSessionCaptureEnabled: ParseUtil.convertStringToBoolean(environmentData.logRocketSessionCaptureEnabled, false)
    };

    const rideWssConfiguration = {
      logRocketProjectKey: environmentData.rideWss.logRocketProjectKey,
      logRocketSessionCaptureEnabled: environmentData.rideWss.logRocketSessionCaptureEnabled
    };

    const rideWssLogRocketConfiguration: LogRocketConfiguration = {
      logRocketProjectKey: rideWssConfiguration.logRocketProjectKey,
      logRocketSessionCaptureEnabled: rideWssConfiguration.logRocketSessionCaptureEnabled
    };

    const amplitudeConfiguration: AmplitudeConfiguration = {
      amplitudeDeploymentKey: environmentData.amplitudeDeploymentKey,
      amplitudeProjectKey: environmentData.amplitudeProjectKey,
      amplitudeSessionCaptureEnabled: ParseUtil.convertStringToBoolean(environmentData.amplitudeSessionCaptureEnabled, false),
      enableFeatureFlagPolling: ParseUtil.convertStringToBoolean(environmentData.enableFeatureFlagPolling, false),
      featureFlagPollingSeconds: ParseUtil.convertStringToNumber(environmentData.featureFlagPollingSeconds, 60)
    };

    let themeLoadPromise: Promise<void>;

    if (isRideWssUrl(window.location.hostname)) {
      // for ride-wss, load theme from compiled bundled css
      themeLoadPromise = new Promise<void>((resolve, reject) => {
        const link = document.createElement('link');
        link.type = 'text/css';
        link.rel = 'stylesheet';
        link.onload = () => resolve();
        link.onerror = () => reject();
        link.href = '/ride-wss-theme.css';
        document.head.appendChild(link);
      });
    } else {
      // fetch configured theme from assets and append to head
      themeLoadPromise = new Promise<void>((resolve, reject) => {
        const link = document.createElement('link');
        link.type = 'text/css';
        link.rel = 'stylesheet';
        link.onload = () => resolve();
        link.onerror = () => reject();
        link.href = `${themeConfiguration.theme}?timestamp=${new Date().getTime()}`;
        document.head.appendChild(link);
      });
    }

    themeLoadPromise.then(() => {
      if (isRideWssUrl(window.location.hostname)) {
        bootstrapApplication(RideWssAppComponent, {
          providers: [
            {
              provide: UZURV_API_CONFIGURATION,
              useValue: {
                claraApiUrl: environmentData.restApiUrl,
                httpDebugMode: environmentData.enableHttpRequestLogging
              }
            },
            // TODO(Ride WSS): These configurations need to be replaced with ride wss specific project keys, etc.
            { provide: APP_CONFIGURATION, useValue: applicationConfiguration },
            { provide: LOG_ROCKET_CONFIGURATION, useValue: rideWssLogRocketConfiguration },

            // All configurations that aren't reliant on the fetched configuration are defined here
            ...(rideWssAppConfig.providers ?? [])
          ]
        });
      } else {
        platformBrowserDynamic([
          { provide: UZURV_REST_CONFIGURATION, useValue: restConfiguration },
          { provide: BUG_SNAG_CONFIGURATION, useValue: bugsnagConfiguration },
          { provide: APP_CONFIGURATION, useValue: applicationConfiguration },
          { provide: LOG_ROCKET_CONFIGURATION, useValue: logRocketConfiguration },
          { provide: AMPLITUDE_CONFIGURATION, useValue: amplitudeConfiguration }
        ]).bootstrapModule(AppModule);
      }
    });
  } catch (error) {
    console.error(error);
  }
};

main();
